import React, {useCallback, useEffect, useState} from "react";
import {apiV2} from "@/api/axiosConfig";
import {SearchInput} from "@/components/search-input";
import {useDebounce} from "use-debounce";
import {useQuery} from "react-query";

type Tag = [string, string]; // [slug, label]

type TagSelectorModalProps = {
    isOpen: boolean;
    onClose: () => void;
    includedTags: Tag[];
    excludedTags: Tag[];
    onIncludedTagsChange: (tags: Tag[]) => void;
    onExcludedTagsChange: (tags: Tag[]) => void;
    mode: "include" | "exclude";
};

export function TagSelectorModal({
    isOpen,
    onClose,
    includedTags,
    excludedTags,
    onIncludedTagsChange,
    onExcludedTagsChange,
    mode
}: TagSelectorModalProps) {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 300);
    const pageSize = 50;

    const currentTags = mode === "include" ? includedTags : excludedTags;
    const otherTags = mode === "include" ? excludedTags : includedTags;

    // Reset page when search changes
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    // Fetch paginated tags
    const {data: paginatedData, isLoading} = useQuery(
        ["incident-tags-paginated", page, debouncedSearch],
        async () => {
            if (debouncedSearch) {
                // Use search endpoint
                const response = await apiV2.get<{tags: Tag[]}>("incident-tags", {
                    params: {search: debouncedSearch, limit: pageSize}
                });
                return {
                    tags: response.data.tags || [],
                    total: response.data.tags?.length || 0,
                    page: 1,
                    pageSize: response.data.tags?.length || 0
                };
            } else {
                // Use paginated endpoint
                const response = await apiV2.get<{
                    tags: Tag[];
                    total: number;
                    page: number;
                    pageSize: number;
                }>("incident-tags/paginated", {
                    params: {page, pageSize}
                });
                return response.data;
            }
        },
        {
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000,
        }
    );

    const handleAddTag = useCallback((tag: Tag) => {
        if (mode === "include") {
            // Don't add if already included
            if (includedTags.some(t => t[0] === tag[0])) return;
            // Remove from excluded if it's there
            onExcludedTagsChange(excludedTags.filter(t => t[0] !== tag[0]));
            onIncludedTagsChange([...includedTags, tag]);
        } else {
            // Don't add if already excluded
            if (excludedTags.some(t => t[0] === tag[0])) return;
            // Remove from included if it's there
            onIncludedTagsChange(includedTags.filter(t => t[0] !== tag[0]));
            onExcludedTagsChange([...excludedTags, tag]);
        }
    }, [mode, includedTags, excludedTags, onIncludedTagsChange, onExcludedTagsChange]);

    const handleRemoveTag = useCallback((slug: string) => {
        if (mode === "include") {
            onIncludedTagsChange(includedTags.filter(t => t[0] !== slug));
        } else {
            onExcludedTagsChange(excludedTags.filter(t => t[0] !== slug));
        }
    }, [mode, includedTags, excludedTags, onIncludedTagsChange, onExcludedTagsChange]);

    const isTagSelected = useCallback((slug: string) => {
        return currentTags.some(t => t[0] === slug);
    }, [currentTags]);

    const isTagInOther = useCallback((slug: string) => {
        return otherTags.some(t => t[0] === slug);
    }, [otherTags]);

    const availableTags = paginatedData?.tags.filter(tag => !isTagSelected(tag[0])) || [];
    const totalPages = debouncedSearch
        ? 1
        : Math.ceil((paginatedData?.total || 0) / pageSize);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-[800px] max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b border-light-light-grey">
                    <h2 className="text-xl font-semibold">
                        {mode === "include" ? "Select Tags to Include" : "Select Tags to Exclude"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-grey hover:text-dark-grey text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col p-4 gap-4">
                    {/* Search */}
                    <div>
                        <SearchInput
                            initialValue={search}
                            onChange={setSearch}
                        />
                    </div>

                    {/* Selected Tags Section */}
                    <div className="flex flex-col gap-2">
                        <h3 className="font-medium">
                            Selected ({currentTags.length})
                        </h3>
                        <div className="border border-light-light-grey rounded p-3 min-h-[100px] max-h-[150px] overflow-y-auto bg-ice">
                            {currentTags.length === 0 ? (
                                <div className="text-grey text-sm text-center py-4">
                                    No tags selected
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {currentTags.map((tag) => (
                                        <div
                                            key={tag[0]}
                                            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                                                mode === "include" ? "bg-light-blue" : "bg-light-red"
                                            }`}
                                        >
                                            <span>{tag[1]}</span>
                                            <button
                                                onClick={() => handleRemoveTag(tag[0])}
                                                className={`font-bold leading-none ${
                                                    mode === "include"
                                                        ? "text-dark-blue hover:text-blue"
                                                        : "text-dark-red hover:text-red"
                                                }`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Available Tags Section */}
                    <div className="flex flex-col gap-2 flex-1 min-h-0">
                        <h3 className="font-medium">
                            Available Tags
                            {!debouncedSearch && paginatedData && ` (${paginatedData.total} total)`}
                        </h3>
                        <div className="border border-light-light-grey rounded overflow-hidden flex flex-col flex-1 min-h-0 bg-white">
                            {isLoading ? (
                                <div className="flex-1 flex items-center justify-center text-grey">
                                    Loading...
                                </div>
                            ) : availableTags.length === 0 ? (
                                <div className="flex-1 flex items-center justify-center text-grey">
                                    No tags available
                                </div>
                            ) : (
                                <div className="flex-1 overflow-y-auto p-3">
                                    <div className="flex flex-wrap gap-2">
                                        {availableTags.map((tag) => (
                                            <button
                                                key={tag[0]}
                                                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                                                    isTagInOther(tag[0])
                                                        ? "bg-desaturated-grey text-grey cursor-not-allowed"
                                                        : mode === "include"
                                                            ? "bg-light-blue hover:bg-blue hover:text-white cursor-pointer"
                                                            : "bg-light-red hover:bg-darker-red hover:text-white cursor-pointer"
                                                }`}
                                                onClick={() => !isTagInOther(tag[0]) && handleAddTag(tag)}
                                                disabled={isTagInOther(tag[0])}
                                            >
                                                {tag[1]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {!debouncedSearch && totalPages > 1 && (
                            <div className="flex justify-center items-center gap-2 pt-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-3 py-1 border border-light-grey rounded hover:bg-ice disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <span className="text-sm">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="px-3 py-1 border border-light-grey rounded hover:bg-ice disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-4 border-t border-light-light-grey">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-blue text-white rounded hover:bg-dark-blue"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
